import {
  DadosBancariosResponseSchema,
  KycCurrentDtoSchema,
  ProfissionalMeiResponseSchema,
  type DadosBancariosResponse,
  type DadosBancariosUpsertInput,
  type KycCurrentDto,
  type ProfissionalMeiResponse,
  type ProfissionalMeiUpsertInput,
  type UpdateFuncoesInput,
  type Funcao,
} from '@lance-x/shared';
import { ApiError, type ApiClient } from '../index';

/**
 * Phase 24 v1.6 — REQ-PIX-01 / D-07. Profissional default-PIX-key surface.
 *
 *  - getDadosBancarios()    → 200 DadosBancariosResponse | 204 → null
 *  - upsertDadosBancarios() → 200 DadosBancariosResponse (replaces any existing)
 *  - deleteDadosBancarios() → 204 (idempotent)
 *
 * 204 handling note: `ApiClient.request()` reads `await res.text()` and
 * `JSON.parse`s when truthy. An empty body (204) leaves `json = undefined`,
 * which `responseSchema.parse(undefined)` would reject. So `getDadosBancarios`
 * uses a custom fetch path that branches on status before parsing. The 204
 * surface is intentional per backend D-07 (semantic "no key set").
 *
 * Server errors propagate as `ApiError` with `status` + `code`:
 *  - 400 pix_key_cpf_mismatch  (PUT only)
 *  - 409 pix_key_em_uso        (PUT only)
 *  - 422 sem_chave_padrao      (from /carteira/sacar/com-chave-padrao, not here)
 */
/**
 * Phase 40 v1.9 — KYC-MOTIVO-01. Grouping for `/profissionais/me/*` reads.
 *
 * Exposed via `apiClient.profissionais.me.kycCurrent()` so the perfil page
 * surfaces the most recent submission status (and motivo when rejeitada)
 * without overloading the flat ProfissionaisEndpoints surface. Kept as a
 * dedicated subclass — the spec accepts more `/me/*` reads later without
 * forcing each into the top-level namespace.
 */
export class ProfissionaisMeEndpoints {
  constructor(private readonly client: ApiClient) {}

  /**
   * GET /profissionais/me/kyc/current — most recent submission status for
   * the authenticated profissional. Returns `{ status: null }` when no
   * submission has ever been made (drives the initial "Verifique sua
   * identidade" CTA on the perfil page).
   */
  kycCurrent(): Promise<KycCurrentDto> {
    return this.client.get('/profissionais/me/kyc/current', {
      responseSchema: KycCurrentDtoSchema,
    });
  }
}

export class ProfissionaisEndpoints {
  /**
   * Phase 40 v1.9 — KYC-MOTIVO-01. `/profissionais/me/*` namespace. Today
   * exposes only `kycCurrent()`; subsequent /me reads can plug in here
   * without polluting the flat surface.
   */
  public readonly me: ProfissionaisMeEndpoints;

  constructor(private readonly client: ApiClient) {
    this.me = new ProfissionaisMeEndpoints(client);
  }

  /**
   * Read the current default PIX key. Returns null on HTTP 204 (no key set);
   * returns the masked DTO on 200. NEVER receives plaintext chave.
   */
  async getDadosBancarios(): Promise<DadosBancariosResponse | null> {
    const url = `${this.client.baseUrl}/profissionais/me/dados-bancarios`;
    const token = await this.getAccessToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (res.status === 204) return null;
    if (!res.ok) {
      // Mirror ApiClient.request error mapping so callers can catch on
      // `instanceof ApiError` uniformly across endpoints.
      const text = await res.text();
      const json = text ? safeJsonParse(text) : undefined;
      throw new ApiError(
        res.status,
        json?.code ?? 'unknown_error',
        json?.message ?? res.statusText,
        json?.details,
      );
    }

    const text = await res.text();
    const json = text ? JSON.parse(text) : undefined;
    return DadosBancariosResponseSchema.parse(json);
  }

  /**
   * Create or replace the default PIX key. Server normalizes + encrypts +
   * hashes + (for tipo=cpf) anti-mule checks. Returns the masked DTO.
   */
  upsertDadosBancarios(
    input: DadosBancariosUpsertInput,
  ): Promise<DadosBancariosResponse> {
    return this.client.put('/profissionais/me/dados-bancarios', input, {
      responseSchema: DadosBancariosResponseSchema,
    });
  }

  /**
   * Remove the default PIX key. Server returns 204; this method resolves to
   * void. Idempotent (clearing a never-set key is a no-op).
   */
  async deleteDadosBancarios(): Promise<void> {
    await this.client.delete<void>('/profissionais/me/dados-bancarios');
  }

  /**
   * Read the registered MEI CNPJ. Returns null on HTTP 204 (no MEI registered);
   * returns masked DTO on 200. NEVER receives plaintext CNPJ.
   */
  async getMei(): Promise<ProfissionalMeiResponse | null> {
    const url = `${this.client.baseUrl}/profissionais/me/mei`;
    const token = await this.getAccessToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (res.status === 204) return null;
    if (!res.ok) {
      const text = await res.text();
      const json = text ? safeJsonParse(text) : undefined;
      throw new ApiError(
        res.status,
        json?.code ?? 'unknown_error',
        json?.message ?? res.statusText,
        json?.details,
      );
    }

    const text = await res.text();
    const json = text ? JSON.parse(text) : undefined;
    return ProfissionalMeiResponseSchema.parse(json);
  }

  /**
   * Register or update MEI CNPJ.
   */
  upsertMei(
    input: ProfissionalMeiUpsertInput,
  ): Promise<ProfissionalMeiResponse> {
    return this.client.put('/profissionais/me/mei', input, {
      responseSchema: ProfissionalMeiResponseSchema,
    });
  }

  /**
   * Remove MEI CNPJ. Server returns 204.
   */
  async deleteMei(): Promise<void> {
    await this.client.delete<void>('/profissionais/me/mei');
  }

  /**
   * Update profissional technical functions.
   */
  updateFuncoes(input: UpdateFuncoesInput): Promise<{ funcoes: Funcao[] }> {
    return this.client.put('/profissionais/me/funcoes', input);
  }

  /**
   * Read the access token via the same accessor the parent client uses.
   * Mirrors the cheap-reflection pattern used in KycEndpoints — the parent's
   * config is private but we need direct fetch for the 204 branch.
   */
  private async getAccessToken(): Promise<string | null> {
    const cfg = (this.client as unknown as {
      config: { getAccessToken: () => string | null | Promise<string | null> };
    }).config;
    return cfg.getAccessToken();
  }
}

function safeJsonParse(text: string): { code?: string; message?: string; details?: unknown } | undefined {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
